<template>
  <main class="auth-form-page">
    <AppNavBar :border="false" />

    <section class="auth-form-head">
      <h1>{{ t('auth.bindEmail') }}</h1>
      <p>{{ t('auth.bindEmailIntro') }}</p>
    </section>

    <section class="auth-form-section">
      <label class="auth-form-label">
        {{ t('auth.emailAddress') }} <em>*</em>
        <small>{{ t('auth.emailForRecovery') }}</small>
      </label>
      <van-field
        v-model="email"
        class="auth-form-field"
        clearable
        type="email"
        :placeholder="t('auth.enterEmailAddress')"
      />
      <van-field
        v-model="code"
        class="auth-form-field"
        clearable
        maxlength="8"
        :placeholder="t('auth.emailCode')"
      >
        <template #button>
          <button type="button" class="auth-code-btn" :disabled="countdown > 0" @click="sendCode">
            {{ countdown > 0 ? `${t('auth.resend')}${countdown}s` : t('auth.getCode') }}
          </button>
        </template>
      </van-field>
    </section>

    <AuthThemeButton block class="auth-submit-btn" :loading="submitting" :disabled="!emailValid || !code.trim()" @click="submitBind">
      {{ t('common.confirm') }}
    </AuthThemeButton>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { showFailToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { devicesBindEmail, devicesBindEmailSend } from '@/api/auth';
import AuthThemeButton from '@/view/auth/components/AuthThemeButton.vue';
import { useProfileStore } from '@/stores/profile';
import { getDeviceId } from '@/utils/device';
import { getRouteState } from '@/utils/route-state';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const pageState = computed(() => getRouteState(route, 'auth-bind-email', {}));

const email = ref(String(profileStore.currentAccount?.email || ''));
const code = ref('');
const submitting = ref(false);
const countdown = ref(0);
let timer = null;

const emailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value));

function startCountdown() {
  countdown.value = 60;
  window.clearInterval(timer);
  timer = window.setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0) {
      window.clearInterval(timer);
      timer = null;
    }
  }, 1000);
}

async function sendCode() {
  if (!emailValid.value) {
    showFailToast(t('auth.enterEmail'));
    return;
  }

  try {
    await devicesBindEmailSend({ token: getDeviceId(), email: email.value.trim() });
    showPassedToast(t('auth.codeSent'));
    startCountdown();
  } catch (_) {
    // handled by interceptor
  }
}

async function submitBind() {
  if (!emailValid.value) {
    showFailToast(t('auth.enterEmail'));
    return;
  }
  if (!code.value.trim()) {
    showFailToast(t('auth.codeRequired'));
    return;
  }

  submitting.value = true;
  try {
    await devicesBindEmail({ token: getDeviceId(), code: code.value.trim() });
    profileStore.setCurrentAccount({
      ...profileStore.currentAccount,
      email: email.value.trim()
    });
    showPassedToast(t('mine.bindSuccess'));
    const back = String(pageState.value.back || '').trim();
    if (back) {
      router.replace(back);
      return;
    }
    router.back();
  } catch (_) {
    // handled by interceptor
  } finally {
    submitting.value = false;
  }
}

onBeforeUnmount(() => {
  window.clearInterval(timer);
});
</script>

<style scoped lang="scss">
@use '@/styles/auth-form.scss';
</style>
